/**
 * Groq Provider
 *
 * Fast cloud LLM provider using Groq API (FREE)
 * Default: openai/gpt-oss-120b model
 */

import { LLMProvider, GenerateOptions } from './base';

export class GroqProvider implements LLMProvider {
  name = 'Groq';

  constructor(
    private apiKey: string,
    private model: string = 'openai/gpt-oss-120b'
  ) {}

  async generateInsights(prompt: string, options?: GenerateOptions): Promise<string> {
    const timeout = options?.timeout || 30000; // 30 seconds default
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'system',
              content: 'You are a sports business analyst that provides insights in JSON format.',
            },
            {
              role: 'user',
              content: prompt,
            },
          ],
          temperature: options?.temperature || 0.7,
          max_tokens: options?.maxTokens || 1000,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Groq API error: ${error.error?.message || response.statusText}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Groq request timed out after ${timeout}ms`);
      }

      throw new Error(`Groq error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async isAvailable(): Promise<boolean> {
    if (!this.apiKey) {
      return false;
    }

    try {
      // Test with a simple models list request
      const response = await fetch('https://api.groq.com/openai/v1/models', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.apiKey}`,
        },
        signal: AbortSignal.timeout(3000),
      });

      return response.ok;
    } catch {
      return false;
    }
  }
}
